import { recipeAllergens, recipeDependencies } from './recipeGraph';
import type { Allergen, Ingredient, MenuItem, Recipe, RecipeComponent, RecipeKind } from './types';
import { nombreDelPlato } from './nombrePlato';
import type { Unit } from './units';

/*
 * Enlace público de la vista de cocina (fase 7, decisión de Pol, 12 oct 2026).
 *
 * Quien escanea el QR de la ficha sin sesión no debería caer en el login: el
 * propietario crea un enlace por receta (un token aleatorio) que se puede
 * desactivar o cambiar. El enlace da acceso de SOLO LECTURA a esa receta y a
 * las preparaciones que usa, y nada más.
 *
 * Esta función es la ÚNICA que decide qué sale por ese enlace. Es una lista
 * BLANCA campo a campo: nunca se copia una receta o un ingrediente entero, así
 * que un campo nuevo de costo, precio o proveedor no puede colarse por
 * descuido. La usan la API y la demo, para que las dos digan lo mismo.
 */

/** Lo que tiene que cumplir un token antes de buscarlo (≥128 bits en base64url). */
export const KITCHEN_TOKEN_RE = /^[A-Za-z0-9_-]{22,64}$/;

/** Enlace de una receta, tal como lo ve quien lo gestiona. */
export interface KitchenLink {
  recipeId: string;
  token: string;
  createdAt: string;
}

/** Una línea de la receta, sin notas ni nada que no sea cocina. */
export type KitchenShareComponent = Pick<RecipeComponent, 'ref' | 'block' | 'qty' | 'unit' | 'wastePct'>;

export interface KitchenShareRecipe {
  recipeId: string;
  name: string;
  kind: RecipeKind;
  code?: string;
  group?: string;
  /** Lista para pintar: URL firmada de corta duración o data-URL. */
  photo?: string;
  steps?: string;
  storage?: Recipe['storage'];
  utensils?: string[];
  /** TODOS los que contiene: los suyos y los heredados de ingredientes y preparaciones. */
  allergens: Allergen[];
  components: KitchenShareComponent[];
  yieldQty: number;
  yieldUnit: Unit;
  portions: number;
  unitWeightG?: number;
}

/**
 * Solo el nombre. Sin la marca, a propósito: mucha gente escribe ahí a quién
 * se lo compra («Tortillería La Esperanza»), y el proveedor no sale por aquí.
 */
export interface KitchenShareIngredient {
  ingredientId: string;
  name: string;
}

export interface KitchenShareView {
  restaurantName?: string;
  recipe: KitchenShareRecipe;
  /** Las preparaciones que usa (también las de dentro de otra). */
  preparations: KitchenShareRecipe[];
  /** Solo los ingredientes que aparecen en estas recetas, solo con su nombre (sin marca). */
  ingredients: KitchenShareIngredient[];
}

function shareRecipe(recipe: Recipe, recipes: readonly Recipe[], ingredients: readonly Ingredient[]): KitchenShareRecipe {
  const out: KitchenShareRecipe = {
    recipeId: recipe.recipeId,
    name: recipe.name,
    kind: recipe.kind,
    allergens: recipeAllergens(recipe.recipeId, recipes, ingredients),
    components: recipe.components.map((c) => ({
      ref: c.ref.kind === 'recipe' ? { kind: 'recipe', recipeId: c.ref.recipeId } : { kind: 'ingredient', ingredientId: c.ref.ingredientId },
      block: c.block,
      qty: c.qty,
      unit: c.unit,
      wastePct: c.wastePct,
    })) as KitchenShareComponent[],
    yieldQty: recipe.yieldQty,
    yieldUnit: recipe.yieldUnit,
    portions: recipe.portions,
  };
  if (recipe.code) out.code = recipe.code;
  if (recipe.group) out.group = recipe.group;
  if (recipe.photo) out.photo = recipe.photo;
  if (recipe.steps) out.steps = recipe.steps;
  if (recipe.storage) {
    const { where, duration, note } = recipe.storage;
    out.storage = { ...(where ? { where } : {}), ...(duration ? { duration } : {}), ...(note ? { note } : {}) };
  }
  if (recipe.utensils?.length) out.utensils = [...recipe.utensils];
  if (recipe.unitWeightG != null) out.unitWeightG = recipe.unitWeightG;
  return out;
}

/**
 * La vista pública de una receta: ella, sus preparaciones y los nombres de
 * los ingredientes que usan. `null` si la receta no existe.
 */
export function buildKitchenShareView(
  recipeId: string,
  recipes: readonly Recipe[],
  ingredients: readonly Ingredient[],
  restaurantName?: string,
  /** Un plato, un nombre (14 oct 2026): con la carta, el plato se llama como en ella. */
  menuItems?: readonly Pick<MenuItem, 'recipeId' | 'name' | 'platformId' | 'active'>[],
): KitchenShareView | null {
  const original = recipes.find((r) => r.recipeId === recipeId);
  if (!original) return null;
  const root = { ...original, name: nombreDelPlato(original, menuItems) };
  const deps = recipeDependencies(recipeId, recipes);
  const usados = new Set<string>();
  for (const r of [root, ...deps]) for (const c of r.components) if (c.ref.kind === 'ingredient') usados.add(c.ref.ingredientId);
  return {
    ...(restaurantName ? { restaurantName } : {}),
    recipe: shareRecipe(root, recipes, ingredients),
    preparations: deps.map((d) => shareRecipe(d, recipes, ingredients)),
    ingredients: ingredients
      .filter((i) => usados.has(i.ingredientId))
      .map((i) => ({ ingredientId: i.ingredientId, name: i.name })),
  };
}
